// Lane G — reusable SoP link widget (SP2) + execution chip (SP3).
//
//   <SopLinks objectType="OPERATION_TYPE" objectRef={record.operation_type_id} />
//       list of linked SoPs with current version, "Open SOP", add / remove with a change reason.
//       compact  → a row of chips + a "+" button (for table cells / headers)
//       readOnly → chips only
//   <SopChip batchNumber="..." operationId={id} />   frozen version for a running batch (SP3 / SE2)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Space, Tag, Tooltip, Typography, message } from 'antd';
import { BookOpen, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { sopApi, openSop, isOk } from '@/utils/sopApi';
import PageModal from '@/components/PageModal';

const { Text } = Typography;

const STATUS_COLOR = { EFFECTIVE: 'green', DRAFT: 'gold', SUPERSEDED: 'orange', RETIRED: 'red' };

export const SopTag = ({ link, onRemove, showInherited = true }) => {
  const cur = link.current;
  const version = link.current_version || link.linked_version || link.version;
  const status = cur?.status || link.linked_status || link.status || (link.has_effective === false ? 'MISSING' : '');
  const inherited = !!link.inherited_from;
  const tip = [
    `${link.sop_no} — ${link.title || ''}`,
    version ? `Current version: v${version}` : 'No effective version',
    inherited ? `Inherited from ${link.inherited_from.replace('_', ' ').toLowerCase()}` : null,
    link.overridden ? 'Overridden by an operation-level link' : null,
    link.context ? `Context: ${link.context}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
      <Tag
        color={link.overridden ? 'default' : STATUS_COLOR[status] || 'blue'}
        style={{ margin: 2, cursor: 'pointer', opacity: inherited && showInherited ? 0.75 : 1, textDecoration: link.overridden ? 'line-through' : 'none' }}
        onClick={() => openSop(link)}
        closable={!!onRemove && !inherited}
        onClose={(e) => {
          e.preventDefault();
          onRemove && onRemove(link);
        }}
        closeIcon={<Trash2 size={11} />}
      >
        <BookOpen size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
        {link.sop_no}
        {version ? ` v${version}` : ''}
        {status && status !== 'EFFECTIVE' ? ` · ${status}` : ''}
      </Tag>
    </Tooltip>
  );
};

const AddLinkModal = ({ open, onCancel, onDone, objectType, objectRef }) => {
  const [sops, setSops] = useState([]);
  const [vocab, setVocab] = useState({ contexts: [] });
  const [sopId, setSopId] = useState();
  const [context, setContext] = useState('EXECUTION');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    sopApi.list({ current: 1 }).then((r) => setSops(r?.data || []));
    sopApi.vocab().then((r) => setVocab(r?.data || { contexts: [] }));
    setSopId(undefined);
    setReason('');
  }, [open]);
  const submit = async () => {
    if (!sopId || !reason.trim()) {
      message.warning('Select a SoP and give a change reason');
      return;
    }
    setSaving(true);
    const r = await sopApi.addLink({ sop_id: sopId, object_type: objectType, object_ref: String(objectRef), context, change_reason: reason.trim() });
    setSaving(false);
    if (isOk(r)) {
      message.success('SoP linked');
      onDone();
    } else message.error(r?.message || 'Could not link SoP');
  };
  return (
    <PageModal
      open={open}
      icon={<BookOpen size={22} />}
      title="Link a SoP"
      description="Attach a standard operating procedure to this record."
      onCancel={onCancel}
      width={560}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={submit}>Link</Button>
        </div>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong>SoP (current versions)</Text>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Search by number or title"
            optionFilterProp="label"
            value={sopId}
            onChange={setSopId}
            options={sops.map((s) => ({
              value: s.sop_id,
              label: `${s.sop_no} v${s.version} — ${s.title}${s.status !== 'EFFECTIVE' ? ` (${s.status})` : ''}`,
            }))}
          />
        </div>
        <div>
          <Text strong>Context</Text>
          <Select style={{ width: '100%' }} value={context} onChange={setContext} options={(vocab.contexts || []).map((c) => ({ value: c.value, label: c.label }))} />
        </div>
        <div>
          <Text strong>Change reason</Text>
          <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this procedure applies here (recorded in the audit trail)" />
        </div>
      </Space>
    </PageModal>
  );
};

export const SopLinks = ({ objectType, objectRef, title = 'SoPs', compact = false, readOnly = false, spread = false, onChange }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    if (!objectType || objectRef === undefined || objectRef === null || objectRef === '') {
      setLinks([]);
      return;
    }
    setLoading(true);
    const r = await sopApi.linksFor(objectType, objectRef);
    setLoading(false);
    setLinks(isOk(r) ? r.data || [] : []);
  }, [objectType, objectRef]);
  useEffect(() => {
    load();
  }, [load]);

  const remove = (link) => {
    let reason = '';
    Modal.confirm({
      title: `Remove ${link.sop_no} from this ${objectType.replace('_', ' ').toLowerCase()}?`,
      content: <Input.TextArea rows={2} placeholder="Reason (required)" onChange={(e) => (reason = e.target.value)} />,
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!reason.trim()) {
          message.warning('A reason is required');
          return Promise.reject();
        }
        const r = await sopApi.removeLink(link.link_id, reason.trim());
        if (isOk(r)) {
          message.success('Link removed');
          load();
          onChange && onChange();
        } else message.error(r?.message || 'Failed');
        return undefined;
      },
    });
  };

  const canAdd = !readOnly && objectRef !== undefined && objectRef !== null && objectRef !== '';
  const body = useMemo(
    () => (
      <>
        {links.length === 0 && !loading && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {canAdd ? 'No SoP linked' : objectRef ? 'No SoP linked' : 'Save first, then link SoPs'}
          </Text>
        )}
        {links.map((l) => (
          <SopTag key={`${l.link_id || 'ref'}-${l.sop_no}-${l.inherited_from || ''}`} link={l} onRemove={readOnly ? undefined : remove} />
        ))}
        {links.some((l) => l.file_url || l.current?.file_url || l.current?.url || l.url) && (
          <Tooltip title="Open the first linked SoP">
            <Button size="small" type="text" icon={<ExternalLink size={13} />} onClick={() => openSop(links.find((l) => l.file_url || l.current?.file_url || l.current?.url || l.url))}>
              Open SOP
            </Button>
          </Tooltip>
        )}
        {canAdd && (
          <Button size="small" type="dashed" icon={<Plus size={12} />} onClick={() => setAdding(true)}>
            {compact ? '' : 'Link SoP'}
          </Button>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [links, loading, canAdd, compact, readOnly]
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, ...(spread ? { justifyContent: 'space-between', gap: 12, padding: '4px 0' } : {}) }}>
      {!compact && (
        <span style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <BookOpen size={14} style={{ flexShrink: 0 }} />
          <Text strong style={{ fontSize: 12.5 }}>{title}</Text>
        </span>
      )}
      {/* spread: the title sits left and everything else together on the right, evenly spaced */}
      {spread ? <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>{body}</div> : body}
      <AddLinkModal
        open={adding}
        onCancel={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          load();
          onChange && onChange();
        }}
        objectType={objectType}
        objectRef={objectRef}
      />
    </div>
  );
};

// SP3: the version frozen on the batch header; opens the frozen version (SE2 — a running batch keeps it).
export const SopChip = ({ batchNumber, operationId, size = 'default' }) => {
  const [sops, setSops] = useState([]);
  useEffect(() => {
    if (!batchNumber || !operationId) return;
    sopApi.frozen(batchNumber, operationId).then((r) => setSops(isOk(r) ? r.data || [] : []));
  }, [batchNumber, operationId]);
  if (!sops.length) return null;
  return (
    <Space wrap size={4}>
      {sops.map((s) => (
        <Tooltip
          key={`${s.sop_no}-${s.inherited_from || ''}`}
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              {`${s.title || ''}\n${s.frozen ? `Frozen at issuance: v${s.version}` : `Current: v${s.version ?? '?'} (batch issued before SoP freeze)`}${
                s.changed_since_issue ? `\nv${s.current_version} is now current — the batch keeps v${s.version}` : ''
              }`}
            </span>
          }
        >
          <Tag
            color={s.changed_since_issue ? 'orange' : 'geekblue'}
            style={{ cursor: s.url || s.file_url ? 'pointer' : 'default', fontSize: size === 'large' ? 16 : 12, padding: size === 'large' ? '4px 10px' : undefined }}
            onClick={() => openSop(s)}
          >
            <BookOpen size={size === 'large' ? 15 : 12} style={{ verticalAlign: -2, marginRight: 4 }} />
            SOP {s.sop_no} v{s.version ?? '?'}
          </Tag>
        </Tooltip>
      ))}
    </Space>
  );
};

export default SopLinks;
